import React, { createContext, useContext, useState, useCallback, useEffect, useRef, startTransition } from 'react';

const LoadingTrackerContext = createContext(null);

// Timeout for objects stuck in loading (2 minutes)
const LOADING_TIMEOUT = 120000; // 2 minutes

export const LoadingTrackerProvider = ({ children }) => {
  const [loadingObjects, setLoadingObjects] = useState(new Set());
  const [failedObjects, setFailedObjects] = useState(new Map()); // Map of objectName -> { error, retryCallback }
  const [loadingProgress, setLoadingProgress] = useState(new Map()); // objectName -> { percent, loaded, total, phase }
  const loadingStartTimes = useRef(new Map()); // Track when each object started loading

  const startLoading = useCallback((objectName) => {
    if (!objectName) return;
    setLoadingObjects(prev => {
      const newSet = new Set(prev);
      newSet.add(objectName);
      return newSet;
    });
    setLoadingProgress((prev) => {
      const next = new Map(prev);
      next.set(objectName, { percent: 0, loaded: 0, total: 0, phase: 'download', indeterminate: true });
      return next;
    });
    // Track when loading started
    loadingStartTimes.current.set(objectName, Date.now());
    // Remove from failed objects when starting to load
    setFailedObjects(prev => {
      const newMap = new Map(prev);
      newMap.delete(objectName);
      return newMap;
    });
  }, []);

  const updateProgress = useCallback((objectName, info = {}) => {
    if (!objectName) return;
    startTransition(() => {
      setLoadingProgress((prev) => {
        const current = prev.get(objectName) || {};
        const nextInfo = { ...current, ...info };
        if (
          current.percent === nextInfo.percent &&
          current.phase === nextInfo.phase &&
          current.indeterminate === nextInfo.indeterminate &&
          current.loaded === nextInfo.loaded &&
          current.cached === nextInfo.cached
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(objectName, nextInfo);
        return next;
      });
    });
  }, []);

  const stopLoading = useCallback((objectName) => {
    setLoadingObjects(prev => {
      const newSet = new Set(prev);
      newSet.delete(objectName);
      return newSet;
    });
    setLoadingProgress((prev) => {
      if (!prev.has(objectName)) return prev;
      const next = new Map(prev);
      next.delete(objectName);
      return next;
    });
    // Remove from start times tracking
    loadingStartTimes.current.delete(objectName);
  }, []);

  const markAsFailed = useCallback((objectName, error, retryCallback, options = {}) => {
    const { backgroundRetry = false } = options;
    setFailedObjects(prev => {
      const newMap = new Map(prev);
      newMap.set(objectName, { error, retryCallback, backgroundRetry });
      return newMap;
    });
    // Keep in loading set while auto-retrying in the background so the scene stays stable
    if (!backgroundRetry) {
      setLoadingObjects(prev => {
        const newSet = new Set(prev);
        newSet.delete(objectName);
        return newSet;
      });
      setLoadingProgress((prev) => {
        if (!prev.has(objectName)) return prev;
        const next = new Map(prev);
        next.delete(objectName);
        return next;
      });
    }
  }, []);

  const clearFailed = useCallback((objectName) => {
    setFailedObjects(prev => {
      const newMap = new Map(prev);
      newMap.delete(objectName);
      return newMap;
    });
  }, []);

  const isLoading = useCallback((objectName) => {
    return loadingObjects.has(objectName);
  }, [loadingObjects]);

  const isFailed = useCallback((objectName) => {
    return failedObjects.has(objectName);
  }, [failedObjects]);

  const getFailedObject = useCallback((objectName) => {
    return failedObjects.get(objectName);
  }, [failedObjects]);

  const getLoadingCount = useCallback(() => {
    return loadingObjects.size;
  }, [loadingObjects]);

  const getFailedCount = useCallback(() => {
    return failedObjects.size;
  }, [failedObjects]);

  const getLoadingList = useCallback(() => {
    return Array.from(loadingObjects);
  }, [loadingObjects]);

  const getFailedList = useCallback(() => {
    return Array.from(failedObjects.entries()).map(([name, data]) => ({
      name,
      error: data.error,
      retryCallback: data.retryCallback,
      backgroundRetry: data.backgroundRetry,
    }));
  }, [failedObjects]);

  // Check for stuck objects periodically
  useEffect(() => {
    const checkStuckObjects = () => {
      const now = Date.now();
      const stuckObjects = [];
      
      loadingObjects.forEach((objectName) => {
        const startTime = loadingStartTimes.current.get(objectName);
        // If no start time recorded, assume it's been loading for a while and mark as stuck
        // This handles objects that were already loading when the component mounted
        if (!startTime || (now - startTime) > LOADING_TIMEOUT) {
          if (!startTime) {
            // Set a start time in the past to mark as stuck
            loadingStartTimes.current.set(objectName, now - LOADING_TIMEOUT - 1000);
          }
          stuckObjects.push(objectName);
        }
      });
      
      // Mark stuck objects as failed
      if (stuckObjects.length > 0) {
        stuckObjects.forEach((objectName) => {
          const error = new Error(`Loading timeout after ${LOADING_TIMEOUT/1000}s - possible WebAssembly memory error or network issue`);
          markAsFailed(objectName, error, () => {
            // Retry callback - just restart loading
            startLoading(objectName);
          });
        });
      }
    };
    
    // Check immediately and then every 10 seconds
    checkStuckObjects();
    const interval = setInterval(checkStuckObjects, 10000);
    
    return () => clearInterval(interval);
  }, [loadingObjects, markAsFailed, startLoading]);

  return (
    <LoadingTrackerContext.Provider
      value={{
        loadingObjects,
        failedObjects,
        startLoading,
        stopLoading,
        updateProgress,
        loadingProgress,
        markAsFailed,
        clearFailed,
        isLoading,
        isFailed,
        getFailedObject,
        getLoadingCount,
        getFailedCount,
        getLoadingList,
        getFailedList,
      }}
    >
      {children}
    </LoadingTrackerContext.Provider>
  );
};

export const useLoadingTracker = () => {
  const context = useContext(LoadingTrackerContext);
  if (!context) {
    throw new Error('useLoadingTracker must be used within LoadingTrackerProvider');
  }
  return context;
};
